import { toast } from "sonner";
import { queryClient } from "@/queryClient";
import { billingKeys } from "@/entities/billing/model/keys";
import { PERMISSIONS } from "@/shared/config/permissions";
import { heldPermissions } from "./heldPermissions";
import {
    BILLING_PATH,
    billingRefusalMessage,
    capacityRefusalMessage,
    parseBillingRefusal,
    parseCapacityRefusal,
    upgradePath,
} from "./billingErrors";

/**
 * Explains a refusal that came from the plan rather than from permissions or a bug, and reports
 * whether it was one so the caller can skip its generic message.
 *
 * Saying "you do not have permission" to someone whose office is behind on payment, or "something went
 * wrong" to someone at their site cap, sends them to the wrong person. What they are told depends on
 * what they may see: only `billing.view` holders read the account's payment standing, and only
 * `billing.manage` holders are told to upgrade it themselves.
 */
export function explainBillingRefusal(error: unknown, options: { silent?: boolean } = {}): boolean {
    const billingRefusal = parseBillingRefusal(error);
    const capacityRefusal = parseCapacityRefusal(error);
    if (!billingRefusal && !capacityRefusal) return false;

    if (billingRefusal) {
        // The account's state just changed under this user: pull the banner and the shrunken
        // permission set now rather than at the next window focus.
        void queryClient.invalidateQueries({ queryKey: billingKeys.all });
        void queryClient.invalidateQueries({ queryKey: ["session", "permissions"] });
    }

    if (options.silent) return true;

    const held = heldPermissions();
    const canViewBilling = held.includes(PERMISSIONS.BillingView);

    if (capacityRefusal) {
        toast.error(
            capacityRefusalMessage(capacityRefusal, held.includes(PERMISSIONS.BillingManage)),
            canViewBilling
                ? {
                    action: {
                        label: "View plan",
                        // Full navigation: this module has no handle on the router.
                        onClick: () => window.location.assign(upgradePath(capacityRefusal)),
                    },
                }
                : undefined,
        );
    } else if (billingRefusal) {
        toast.error(
            billingRefusalMessage(billingRefusal, canViewBilling),
            // A lapsed trial has a next step; a missed payment's is already in the words.
            canViewBilling && billingRefusal.kind === "trial_ended"
                ? { action: { label: "Choose a plan", onClick: () => window.location.assign(BILLING_PATH) } }
                : undefined,
        );
    }
    return true;
}
